import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * LA RÉGION D'ANNONCE UNIQUE — item D.949.
 *
 * ⚠️⚠️ POURQUOI UNE SEULE. Le poste portait TROIS régions live implicites rien
 * que sur l'écran carte (deux `role="status"`, un `role="alert"`), dont une
 * sur le bandeau des repères ENTIER — qui change à chaque `mousemove` : un
 * lecteur d'écran annonçait les coordonnées en rafale, et le poste devenait
 * inutilisable à VoiceOver dès le premier survol. Plusieurs régions annoncent
 * aussi le même événement DEUX fois quand deux composants le reflètent.
 *
 * Une seule région, polie (`polite`), au niveau du poste : les composants
 * APPELLENT `annoncer(...)`, ils ne portent plus de région. Les ERREURS
 * gardent leur `role="alert"` local — rares, assertives, et liées au geste
 * qui vient d'échouer.
 *
 * ⚠️ LE MÊME MESSAGE DEUX FOIS DOIT S'ANNONCER DEUX FOIS. Un lecteur d'écran
 * ne relit une région que si son CONTENU change : « 3 parcelles retenues »
 * suivi de « 3 parcelles retenues » resterait muet la seconde fois. On
 * alterne un suffixe invisible (espace insécable) pour forcer la relecture.
 */
const ContexteAnnonces = createContext<(message: string) => void>(() => {});

export function useAnnoncer(): (message: string) => void {
  return useContext(ContexteAnnonces);
}

export function PorteurDAnnonces({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const bascule = useRef(false);
  const annoncer = useCallback((m: string) => {
    bascule.current = !bascule.current;
    setMessage(m + (bascule.current ? ' ' : ''));
  }, []);
  const valeur = useMemo(() => annoncer, [annoncer]);
  return (
    <ContexteAnnonces.Provider value={valeur}>
      {children}
      {/* Visuellement cachée, jamais `display:none` — une région masquée
          n'annonce rien. */}
      <div className="annonce-unique" role="status" aria-live="polite">{message}</div>
    </ContexteAnnonces.Provider>
  );
}
